<!--
 *@Author: 作者名 lx
 *@Date: 2026-01-10 11:29:23
 *@Description: 归档
-->
<script setup lang="ts">
const config = useRuntimeConfig()

// 类型定义
interface ArticleType {
  TypeId: number
  Name: string
  Slug: string
  Icon: string | null
  ArticleCount: number
}

interface YearStat {
  Year: string
  ArticleCount: number
}

interface SeriesItem {
  SeriesId: number
  Name: string
  Slug: string
  TypeId: number
  ArticleCount: number
}

interface TagItem {
  TagId: number
  Name: string
  Slug: string
  Color: string | null
  ArticleCount: number
}

interface YearArticle {
  ArticleId: number
  Title: string
  Slug: string
  Summary: string | null
  TypeId: number
  TypeName: string
  TypeSlug: string
  CreatedAt: string
}

interface ArchiveData {
  types: ArticleType[]
  years: YearStat[]
  series: SeriesItem[]
  tags: TagItem[]
}

/**
 * 【性能优化】带缓存的数据获取
 * 使用 useState 做全局缓存，缓存 1 小时避免频繁请求
 */
const CACHE_DURATION = 60 * 60 * 1000 // 1 小时

interface CachedData<T> {
  data: T
  timestamp: number
}

// 全局缓存状态
const archiveCache = useState<CachedData<ArchiveData> | null>('archive-data-cache', () => null)

// 检查缓存是否有效
const isCacheValid = <T>(cache: CachedData<T> | null): cache is CachedData<T> => {
  if (!cache) return false
  return Date.now() - cache.timestamp < CACHE_DURATION
}

// 获取归档数据（带缓存）
const { data, status, error } = await useAsyncData('archive-data', async () => {
  // 检查缓存
  if (isCacheValid(archiveCache.value)) {
    return archiveCache.value.data
  }
  
  // 请求新数据
  const result = await $fetch<ArchiveData>(`${config.public.apiBase}/archive`)
  
  // 更新缓存
  archiveCache.value = {
    data: result,
    timestamp: Date.now()
  }
  
  return result
}, { server: true, dedupe: 'defer' })

const pending = computed(() => status.value === 'pending')

// 展开的年份
const expandedYears = ref<Set<string>>(new Set())

// 年份文章缓存（使用 useState 持久化）
const yearArticlesCache = useState<Record<string, YearArticle[]>>('archive-year-articles-cache', () => ({}))
const loadingYears = ref<Set<string>>(new Set())

// 切换年份展开
const toggleYear = async (year: string) => {
  if (expandedYears.value.has(year)) {
    expandedYears.value.delete(year)
    expandedYears.value = new Set(expandedYears.value)
  } else {
    expandedYears.value.add(year)
    expandedYears.value = new Set(expandedYears.value)
    
    // 如果还没加载过，则请求数据
    if (!yearArticlesCache.value[year]) {
      loadingYears.value.add(year)
      try {
        const res = await $fetch<{ year: string; articles: YearArticle[] }>(
          `${config.public.apiBase}/archive/year/${year}`
        )
        yearArticlesCache.value[year] = res.articles
      } catch (e) {
        console.error('加载年份文章失败', e)
      } finally {
        loadingYears.value.delete(year)
        loadingYears.value = new Set(loadingYears.value)
      }
    }
  }
}

// 格式化日期
const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return `${date.getMonth() + 1}月${date.getDate()}日`
}

// SEO
useHead({
  title: '归档',
  meta: [
    { name: 'description', content: '文章归档 - 按分类、年份、系列、标签浏览' }
  ]
})
</script>

<template>
  <div class="archive-page">
    <h1 class="page-title">归档</h1>

    <!-- 加载状态 -->
    <div v-if="pending" class="loading-state">
      <span class="loading-spinner"></span>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-state">
      <p>加载失败，请稍后重试</p>
    </div>

    <!-- 主内容 -->
    <div v-else-if="data" class="archive-content">
      <!-- 上半部分：分类 + 文章(年份) -->
      <div class="archive-grid">
        <!-- 分类 -->
        <section class="archive-section">
          <h2 class="section-title">分类</h2>
          <ul class="stat-list">
            <li v-for="type in data.types" :key="type.TypeId" class="stat-item">
              <NuxtLink :to="`/blog/type/${type.Slug}`" class="stat-link">
                <span class="stat-name">{{ type.Name }}</span>
                <span class="stat-count">{{ type.ArticleCount }}</span>
              </NuxtLink>
            </li>
          </ul>
        </section>

        <!-- 文章(按年份) -->
        <section class="archive-section">
          <h2 class="section-title">文章</h2>
          <ul class="stat-list year-list">
            <li v-for="year in data.years" :key="year.Year" class="year-item">
              <div 
                class="stat-item year-header" 
                :class="{ expanded: expandedYears.has(year.Year) }"
                @click="toggleYear(year.Year)"
              >
                <span class="stat-name">
                  <span class="expand-icon">{{ expandedYears.has(year.Year) ? '−' : '+' }}</span>
                  {{ year.Year }}
                </span>
                <span class="stat-count">{{ year.ArticleCount }}</span>
              </div>
              
              <!-- 展开的文章列表 -->
              <Transition name="expand">
                <div v-if="expandedYears.has(year.Year)" class="year-articles">
                  <div v-if="loadingYears.has(year.Year)" class="articles-loading">
                    加载中...
                  </div>
                  <ul v-else-if="yearArticlesCache[year.Year]" class="article-list">
                    <li v-for="article in yearArticlesCache[year.Year]" :key="article.ArticleId">
                      <NuxtLink :to="`/blog/${article.Slug}`" class="article-link">
                        <span class="article-date">{{ formatDate(article.CreatedAt) }}</span>
                        <span class="article-title">{{ article.Title }}</span>
                      </NuxtLink>
                    </li>
                  </ul>
                </div>
              </Transition>
            </li>
          </ul>
        </section>
      </div>

      <!-- 系列 -->
      <section class="archive-section series-section">
        <h2 class="section-title">系列</h2>
        <ul class="stat-list">
          <li v-for="series in data.series" :key="series.SeriesId" class="stat-item">
            <NuxtLink :to="`/series/${series.Slug}`" class="stat-link">
              <span class="stat-name">{{ series.Name }}</span>
              <span class="stat-count">{{ series.ArticleCount }}</span>
            </NuxtLink>
          </li>
        </ul>
      </section>

      <!-- 标签 -->
      <section class="archive-section tags-section">
        <h2 class="section-title">标签</h2>
        <div class="tags-cloud">
          <NuxtLink 
            v-for="tag in data.tags" 
            :key="tag.TagId"
            :to="`/blog/tag/${tag.Slug}`"
            class="tag-item"
          >
            {{ tag.Name }}
          </NuxtLink>
        </div>
      </section>
    </div>
  </div>
</template>

<style lang="css" scoped>
.archive-page {
  max-width: 900px;
  margin: 0 auto;
  min-height: 80vh;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 2.5rem;
  color: var(--text-color);
}

/* 加载和错误状态 */
.loading-state,
.error-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 40vh;
  color: var(--muted-color);
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--border-color);
  border-top-color: var(--text-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 主内容区域 */
.archive-content {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

/* 上部分双列布局 */
.archive-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
}

@media (max-width: 640px) {
  .archive-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
}

/* Section 样式 */
.archive-section {
  /* 无特殊样式 */
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border-color);
}

/* 统计列表 */
.stat-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0;
  border-bottom: 1px dashed var(--border-color);
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-link {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  text-decoration: none;
  color: var(--text-color);
  transition: color 0.2s;
}

.stat-link:hover {
  color: var(--primary-color, #4183c4);
}

.stat-name {
  font-size: 0.95rem;
}

.stat-count {
  font-size: 0.85rem;
  color: var(--muted-color);
}

/* 年份列表 */
.year-item {
  border-bottom: none;
}

.year-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
  padding: 0.6rem 0.5rem;
  margin: 0 -0.5rem;
  border-radius: 4px;
}

.year-header:hover {
  background-color: rgba(125, 125, 125, 0.08);
}

.expand-icon {
  display: inline-block;
  width: 1rem;
  text-align: center;
  margin-right: 0.25rem;
  font-weight: 400;
  color: var(--muted-color);
}

/* 年份展开的文章列表 */
.year-articles {
  padding-left: 1.5rem;
  overflow: hidden;
}

.articles-loading {
  padding: 0.5rem 0;
  color: var(--muted-color);
  font-size: 0.85rem;
}

.article-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.article-list li {
  padding: 0.4rem 0;
}

.article-link {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  text-decoration: none;
  color: var(--text-color);
  font-size: 0.9rem;
  transition: color 0.2s;
}

.article-link:hover {
  color: var(--primary-color, #4183c4);
}

.article-date {
  color: var(--muted-color);
  font-size: 0.8rem;
  white-space: nowrap;
}

.article-title {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 展开动画 */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 500px;
}

/* 系列部分 */
.series-section .stat-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0 2rem;
}

/* 标签云 */
.tags-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
}

.tag-item {
  text-decoration: none;
  color: var(--text-color);
  font-size: 0.9rem;
  transition: color 0.2s;
  white-space: nowrap;
}

.tag-item:hover {
  color: var(--primary-color, #4183c4);
}

/* 暗色模式适配 */
:global(html[data-theme="dark"]) .year-header:hover {
  background-color: rgba(255, 255, 255, 0.05);
}
</style>